/* ──────────────────────────────────────────────────────────────────────
 *  DimensionActionModal — explainer + CTA for a dimension advance action.
 *
 *  Pattern 1 (Skip 2026-06-15): the modal explains the action and routes
 *  the user to the real surface where they do it. Some actions instead use
 *  an in-place tray ('tray' handler) — those land in pass two, so for now
 *  the CTA shows as "coming soon." Info-only actions (e.g. the in-person
 *  civic act) show the explainer with no destination yet.
 *
 *  i18n NOTE: copy is English literal (prototype, see dimensionAdvanceActions).
 * ────────────────────────────────────────────────────────────────────── */

import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { DIMENSION_CONFIG } from '../DimensionRadar/dimensionConfig';
import type { DimKey } from '../DimensionRadar/dimensionConfig';
import type { AdvanceAction } from '../../Data/Onboarding/dimensionAdvanceActions';
import styles from './DimensionActionModal.module.css';

const DIM_NAMES: Record<DimKey, string> = {
  mind: 'Mind',
  voice: 'Voice',
  body: 'Body',
  heart: 'Heart',
  wings: 'Wings',
  roots: 'Roots',
};

interface DimensionActionModalProps {
  dim: DimKey;
  action: AdvanceAction;
  onClose: () => void;
}

export default function DimensionActionModal({ dim, action, onClose }: DimensionActionModalProps) {
  const navigate = useNavigate();
  const cfg = DIMENSION_CONFIG[dim];
  const Icon = cfg.icon;
  const handler = action.handler;

  /* Esc closes. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handlePrimary = useCallback(() => {
    if (handler?.kind === 'route') {
      navigate(handler.to);
      onClose();
    }
  }, [handler, navigate, onClose]);

  return (
    <div className={styles.backdrop} role="presentation" onClick={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dim-action-title"
        style={{ '--dim-color': cfg.color } as React.CSSProperties}
        onClick={(e) => e.stopPropagation()}
      >
        <button className={styles.close} onClick={onClose} aria-label="Close" type="button">
          ✕
        </button>

        <div className={styles.eyebrow}>
          <Icon size={16} color={cfg.color} strokeWidth={2.2} aria-hidden="true" />
          <span>{DIM_NAMES[dim]}</span>
        </div>

        <h2 className={styles.title} id="dim-action-title">{action.label}</h2>

        {action.blurb && <p className={styles.blurb}>{action.blurb}</p>}

        <div className={styles.footer}>
          {handler?.kind === 'route' && (
            <button className={styles.primary} onClick={handlePrimary} type="button">
              {handler.cta}
              <ArrowRight size={16} strokeWidth={2.4} aria-hidden="true" />
            </button>
          )}

          {handler?.kind === 'tray' && (
            <button className={styles.primary} type="button" disabled>
              {handler.cta}
              <span className={styles.soon}>Coming soon</span>
            </button>
          )}

          {(!handler || handler.kind === 'info') && (
            <span className={styles.infoNote}>Available soon</span>
          )}

          <button className={styles.secondary} onClick={onClose} type="button">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
